import {
  workV4Query,
  getValueFromHTMLStringByAttribute,
  IWorkUser,
  useCurrentUserInfo,
} from '@diana/core';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { WORK_PAGE_NS } from '../i18n';
import { CommentsRichTextEditor, ICommentsRichTextEditorRef } from './CommentsRichTextEditor';
import { dataMentionAttribute, dataMentionUserIdAttribute } from './Support';

export interface ICommentsNewCommentBoxProps {
  workId: string;
  users: IWorkUser[];
}

export function CommentsNewCommentBox(props: ICommentsNewCommentBoxProps) {
  const { workId, users } = props;

  const ref = useRef<ICommentsRichTextEditorRef>(null);
  const info = useCurrentUserInfo();
  const { t } = useTranslation(WORK_PAGE_NS);

  const addMessageQuery = workV4Query.addWorkConversationMessage({ workId });
  const { mutateAsync: sendMessageFn, isPending: isSendingMessage } = useMutation({
    mutationKey: addMessageQuery.key,
    mutationFn: addMessageQuery.fn,
    onSuccess: () => ref.current?.clearTextEditor(),
  });

  const onClickSend = useCallback(
    (text: string) => {
      if (isSendingMessage || !info || text === '') return;

      const users = getValueFromHTMLStringByAttribute({
        html: text,
        querySelector: `[${dataMentionAttribute}][${dataMentionUserIdAttribute}]`,
        htmlAttribute: dataMentionUserIdAttribute,
        unique: true,
      });

      sendMessageFn({
        message: {
          messageText: text,
          mentionedUsers: users,
        },
      });
    },
    [info, isSendingMessage, sendMessageFn],
  );

  return (
    <CommentsRichTextEditor
      ref={ref}
      users={users}
      placeholder={t('commentsDrawer.inputPlaceholder')}
      sendButton={{
        fn: onClickSend,
        isLoading: isSendingMessage,
        label: t('commentsDrawer.send'),
      }}
    />
  );
}
